import { __ } from '@wordpress/i18n';
import PropTypes from 'prop-types';
import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';

// Components.
import Help from '@/components/help';
import Select from '@/components/select';
import Social from '@/components/social';

// Config.
import { getChartOptions } from '@/config/charts';
import outputTypes from '@/config/output-types';
import { DATA_SHAPE, FILTERS_SHAPE } from '@/config/prop-types';

// Services.
import formatChartData from '@/services/data/format-chart-data';
import getChartSubtitle from '@/services/filters/get-chart-subtitle';
import getChartTitle from '@/services/filters/get-chart-title';
import getFilterUrl from '@/services/filters/get-filter-url';
import modifyFilters from '@/services/filters/modify-filters';

const Chart = ({
  data,
  disambiguatedFilters,
  filters,
}) => {
  const navigate = useNavigate();
  const {
    outputType,
    sourceOfFunds,
    tab,
    yearType,
  } = filters;

  // Only show the dropdown to change output type if we are on one of the first two tabs.
  const showOutputTypePicker = tab <= 1;

  // Use a line chart unless we're viewing a specific year or comparing.
  const chartType = yearType === 'range' ? 'line' : 'bar';

  // Get chart options config object based on what is being displayed.
  const chartOptions = getChartOptions(chartType, outputType, tab);

  // Create the chart data object to pass in.
  const chartData = formatChartData(data, tab);

  // Handle changes to the output type.
  const handleOutputTypeChange = (next) => navigate(getFilterUrl(modifyFilters(filters, 'set', 'outputType', next)));

  /*
   * Filter the output types to disallow population-adjusted types if
   * sourceOfFunds is not exactly [allSources].
   */
  const outputTypesFiltered = !sourceOfFunds.includes('allSources') || sourceOfFunds.length !== 1
    ? outputTypes.filter(({ value }) => !value.includes('pop'))
    : [...outputTypes];

  return (
    <>
      <h2 className="hst-chart__label">
        {getChartTitle(disambiguatedFilters)}
      </h2>
      <div className="hst-chart__description">
        {getChartSubtitle(disambiguatedFilters)}
      </div>
      <div className="hst-chart__display-container">
        {showOutputTypePicker ? (
          <>
            <Select
              hiddenLabel
              label={__('Select output type', 'hst')}
              onChange={handleOutputTypeChange}
              options={outputTypesFiltered}
              value={outputType}
            />
            <Help text={__('Use this dropdown to change the output scale.', 'hst')} />
          </>
        ) : null}
        <Social />
      </div>
      <div className="hst-chart__canvas">
        {chartType === 'bar' ? (
          <Bar data={chartData} options={chartOptions} />
        ) : (
          <Line data={chartData} options={chartOptions} />
        )}
      </div>
    </>
  );
};

Chart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape(DATA_SHAPE)).isRequired,
  disambiguatedFilters: PropTypes.shape(FILTERS_SHAPE).isRequired,
  filters: PropTypes.shape(FILTERS_SHAPE).isRequired,
};

export default Chart;
